import { Component, Inject, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Utils } from '../../../shared/utils';
import { InvestmentInCompaniesDepositsModel, InvestmentInCompaniesModel } from './investment-incompanies.model';
import { InvestmentInCompaniesService } from './investment-incompanies.service';
import { NotificationService } from '../../../modules_v2/shared/services/notification.service';
import { ConfirmService } from '../../../modules_v2/shared/services/confirm.service';
import { ConfirmDialogDetail } from '../../../modules_v2/shared/components/confirm-dialog.component';
import {
  EMPTY_GUID,
  INVESTMENT_COMPANY_PAY_METHOD_OPTIONS,
  INVESTMENT_COMPANY_PAY_METHODS,
  roundMoney
} from '../../../modules_v2/investment/investment-company/models/investment-company.model';
import {
  InvestmentCompanyLookupsFacade,
  InvestmentCompanyOption,
  InvestmentCompanyPayKind
} from '../../../modules_v2/investment/investment-company/services/investment-company-lookups.facade';

export interface InvestmentInCompaniesDialogPayData {
  investment: InvestmentInCompaniesModel;
  editUid?: string;
}

const KIND_CONTROLS: Record<InvestmentCompanyPayKind, string> = {
  bankAccounts: 'bankAccount',
  paymentCards: 'paymentCard',
  cashRegisters: 'cashRegister',
  partners: 'partner',
  creditPolicies: 'creditPolicy',
};

@Component({
    selector: 'convfact-investmentincompaniespay',
    templateUrl: './investment-incompanies-dialog-pay.component.html',
    styleUrls: ['./investment-incompanies-dialog-pay.component.scss'],
    standalone: false
})
export class InvestmentInCompaniesDialogPayComponent implements OnInit {

  readonly methods = INVESTMENT_COMPANY_PAY_METHODS;
  readonly methodOptions = INVESTMENT_COMPANY_PAY_METHOD_OPTIONS;

  readonly methodConfig = {
    label: 'Tipo de pago',
    placeholder: 'Seleccionar tipo de pago…',
    required: true,
    options: INVESTMENT_COMPANY_PAY_METHOD_OPTIONS.map(method => ({ id: method, display: method })),
    strict: true,
    onChange: () => this.onTypePayChange(),
  };

  public investment: InvestmentInCompaniesModel = this.data.investment;

  public form: UntypedFormGroup;

  public ready = false;
  public loadError = false;
  public saving = false;
  public savingMessage = '';
  public submitted = false;
  public saveError = '';

  public deposits: InvestmentInCompaniesDepositsModel[] = [];
  public editing: InvestmentInCompaniesDepositsModel | null = null;

  public options: Record<InvestmentCompanyPayKind, InvestmentCompanyOption[]> = {
    bankAccounts: [],
    paymentCards: [],
    cashRegisters: [],
    partners: [],
    creditPolicies: [],
  };
  public loadingOptions: Partial<Record<InvestmentCompanyPayKind, boolean>> = {};

  public configs: Record<InvestmentCompanyPayKind, any> = {
    bankAccounts: { label: 'Cuenta bancaria', placeholder: 'Seleccionar cuenta…', required: true, autoSelectSingle: true, strict: true, options: [] },
    paymentCards: { label: 'Tarjeta de crédito', placeholder: 'Seleccionar tarjeta…', required: true, autoSelectSingle: true, strict: true, options: [] },
    cashRegisters: { label: 'Caja / Efectivo', placeholder: 'Seleccionar caja…', required: true, autoSelectSingle: true, strict: true, options: [] },
    partners: { label: 'Socio', placeholder: 'Seleccionar socio…', required: true, autoSelectSingle: true, strict: true, options: [] },
    creditPolicies: { label: 'Póliza de crédito', placeholder: 'Seleccionar póliza…', required: true, autoSelectSingle: true, strict: true, options: [] },
  };

  private readonly missingLabels: Record<InvestmentCompanyPayKind, string> = {
    bankAccounts: 'Cuenta bancaria',
    paymentCards: 'Tarjeta de crédito',
    cashRegisters: 'Caja',
    partners: 'Socio',
    creditPolicies: 'Póliza de crédito',
  };

  private readonly emptyHints: Record<InvestmentCompanyPayKind, string> = {
    bankAccounts: 'No tienes cuentas bancarias.',
    paymentCards: 'No tienes tarjetas de crédito.',
    cashRegisters: 'No tienes cajas registradas.',
    partners: 'No tienes socios en esta actividad.',
    creditPolicies: 'No tienes pólizas de crédito.',
  };

  private changed = false;

  private requestedEditDone = false;
  private readonly optionRequests = new Map<InvestmentCompanyPayKind, Promise<void>>();
  private readonly pendingSelection = new Map<InvestmentCompanyPayKind, { id: any; label: string }>();
  private readonly loadedKinds = new Set<InvestmentCompanyPayKind>();

  static open(dialog: MatDialog, data: InvestmentInCompaniesDialogPayData): Observable<boolean> {
    return dialog.open<InvestmentInCompaniesDialogPayComponent, InvestmentInCompaniesDialogPayData, boolean>(InvestmentInCompaniesDialogPayComponent, {
      width: '680px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    }).afterClosed().pipe(map(result => result === true));
  }

  constructor(
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<InvestmentInCompaniesDialogPayComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: InvestmentInCompaniesDialogPayData,
    private investmentInCompaniesService: InvestmentInCompaniesService,
    private lookups: InvestmentCompanyLookupsFacade,
    private notify: NotificationService,
    private confirm: ConfirmService,
  ) {
    this.form = this.fb.group({
      typePay: [null],
      depositDate: [new Date()],
      amount: [null],
      commissions: [null],
      bankAccount: [null],
      paymentCard: [null],
      cashRegister: [null],
      partner: [null],
      creditPolicy: [null],
    });
  }

  get subtitle(): string {
    if (!this.investment) return '';
    const parts = [
      this.investment.investmentDescription,
      this.investment.investmentDate ? formatDate(this.investment.investmentDate, 'dd/MM/yyyy', 'es-ES') : null,
    ];
    return parts.filter(Boolean).join(' · ');
  }

  get typePayControl(): UntypedFormControl {
    return this.form.get('typePay') as UntypedFormControl;
  }

  get depositDateControl(): UntypedFormControl {
    return this.form.get('depositDate') as UntypedFormControl;
  }

  get amountControl(): UntypedFormControl {
    return this.form.get('amount') as UntypedFormControl;
  }

  get commissionsControl(): UntypedFormControl {
    return this.form.get('commissions') as UntypedFormControl;
  }

  public control(kind: InvestmentCompanyPayKind): UntypedFormControl {
    return this.form.get(KIND_CONTROLS[kind]) as UntypedFormControl;
  }

  public selectedId(kind: InvestmentCompanyPayKind): any {
    const value = this.control(kind).value;
    return value && typeof value === 'object' && value.id !== undefined && value.id !== null && value.id !== '' ? value.id : null;
  }

  get typePay(): string | null {
    const value = this.typePayControl.value;
    return value && typeof value === 'object' && value.id ? value.id : null;
  }

  get total(): number {
    return roundMoney(this.investment ? this.investment.investmentValue : 0);
  }

  get paidAmount(): number {
    return roundMoney(this.deposits.reduce((acc, d) => acc + (Number(d.amount) || 0), 0));
  }

  get remaining(): number {
    return roundMoney(this.total - this.paidAmount);
  }

  get isPaid(): boolean {
    return this.total > 0 && this.remaining <= 0;
  }

  get maxAmount(): number {
    return roundMoney(this.remaining + (this.editing ? Number(this.editing.amount) || 0 : 0));
  }

  get showForm(): boolean {
    return !this.isPaid || !!this.editing;
  }

  get missingTypePay(): boolean { return !this.typePay; }
  get missingDate(): boolean { return !this.depositDateControl.value; }
  get missingBankAccount(): boolean { return this.typePay === this.methods.transfer && !this.selectedId('bankAccounts'); }
  get missingPaymentCard(): boolean { return this.typePay === this.methods.card && !this.selectedId('paymentCards'); }
  get missingCash(): boolean { return this.typePay === this.methods.cash && !this.selectedId('cashRegisters'); }
  get missingPartner(): boolean { return this.typePay === this.methods.partner && !this.selectedId('partners'); }
  get missingCreditPolicy(): boolean { return this.typePay === this.methods.creditPolicy && !this.selectedId('creditPolicies'); }

  get invalidAmount(): boolean {
    const value = Number(this.amountControl.value);
    return !value || value <= 0 || value > this.maxAmount + 0.001;
  }

  get invalidCommissions(): boolean {
    const commissions = this.commissionsControl.value;
    return this.typePay === this.methods.transfer && this.hasValue(commissions) && (isNaN(Number(commissions)) || Number(commissions) < 0);
  }

  get amountError(): string {
    const value = Number(this.amountControl.value);
    if (!value || value <= 0) return 'Debe ser mayor que cero';
    return `No puedes pagar más de lo pendiente (${Utils.FormatMoney(this.maxAmount)} €)`;
  }

  get hasErrors(): boolean {
    return this.missingTypePay || this.missingDate || this.missingBankAccount || this.missingPaymentCard
      || this.missingCash || this.missingPartner || this.missingCreditPolicy || this.invalidAmount || this.invalidCommissions;
  }

  public ngOnInit(): void {
    this.loadDeposits(true);
  }

  public retry(): void {
    this.loadDeposits(true);
  }

  public assignRemaining(): void {
    this.amountControl.setValue(this.maxAmount > 0 ? this.maxAmount : null);
  }

  public onTypePayChange(): void {
    const kind = this.lookupKind(this.typePay);
    if (kind) this.ensureOptions(kind);
  }

  public startEdit(deposit: InvestmentInCompaniesDepositsModel): void {
    if (this.saving) return;
    this.editing = deposit;
    this.submitted = false;
    this.saveError = '';
    this.pendingSelection.clear();

    const type = this.methodOptions.indexOf(deposit.typePay) !== -1 ? deposit.typePay : null;
    this.form.patchValue({
      typePay: type ? { id: type, display: type } : null,
      depositDate: deposit.depositDate ? new Date(deposit.depositDate) : null,
      amount: Number(deposit.amount) || null,
      commissions: Number(deposit.commissions) || null,
      bankAccount: null,
      paymentCard: null,
      cashRegister: null,
      partner: null,
      creditPolicy: null,
    }, { emitEvent: false });

    const info = deposit.bankAccountInfo;
    this.rememberSelection('bankAccounts', this.guidOrNull(deposit.bankAccountsUid), info);
    this.rememberSelection('paymentCards', this.guidOrNull(deposit.paymentCardsUid), info);
    this.rememberSelection('cashRegisters', deposit.efectivoId || null, info);
    this.rememberSelection('partners', deposit.partnersId || null, info);
    this.rememberSelection('creditPolicies', deposit.polizaCreditoId || null, info);

    this.onTypePayChange();
    this.applyPendingSelections();
  }

  public cancelEdit(): void {
    this.editing = null;
    this.resetForm();
  }

  public save(): void {
    if (this.saving) return;
    this.submitted = true;
    this.saveError = '';
    if (this.hasErrors) return;

    const editing = !!this.editing;
    const payload = this.buildPayload();
    const request = editing
      ? this.investmentInCompaniesService.depositUpdate(payload)
      : this.investmentInCompaniesService.depositCreate(payload);

    this.saving = true;
    this.savingMessage = editing ? 'Actualizando pago…' : 'Registrando pago…';
    this.dialogRef.disableClose = true;

    request
      .then(ok => {
        if (ok !== true) throw new Error();
        this.changed = true;
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.notify.ok(editing ? 'Pago actualizado correctamente' : 'Pago registrado correctamente');
        if (!editing || this.data.editUid) {
          this.dialogRef.close(true);
          return;
        }
        this.editing = null;
        this.resetForm();
        this.loadDeposits(false);
      })
      .catch(error => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.saveError = (error && error.error && error.error.message)
          || (editing ? 'Ha ocurrido un error inesperado al actualizar el pago' : 'Ha ocurrido un error inesperado al añadir el pago');
      });
  }

  public deleteDeposit(deposit: InvestmentInCompaniesDepositsModel): void {
    if (this.saving) return;
    this.confirm.open({
      title: 'Eliminar pago',
      subtitle: this.subtitle || undefined,
      message: 'Se eliminará el pago de la inversión y su asiento contable. Si estaba conciliado, el movimiento bancario volverá a quedar pendiente. Esta acción no se puede deshacer.',
      tone: 'danger',
      details: this.depositDetails(deposit),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando pago…',
      errorText: 'Ha ocurrido un error al eliminar el pago',
      action: () => this.investmentInCompaniesService.depositDelete(deposit.uid).then(ok => { if (ok !== true) throw new Error(); }),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.changed = true;
      this.notify.ok('Pago eliminado correctamente');
      if (this.editing && this.editing.uid === deposit.uid) this.cancelEdit();
      this.loadDeposits(false);
    });
  }

  public closeDialog(): void {
    if (this.saving) return;
    this.dialogRef.close(this.changed);
  }

  public formatMoney(value: number): string {
    return Utils.FormatMoney(Number(value) || 0) + ' €';
  }

  public trackByUid(_: number, deposit: InvestmentInCompaniesDepositsModel): string {
    return deposit.uid;
  }

  private loadDeposits(initial: boolean): void {
    if (initial) {
      this.ready = false;
      this.loadError = false;
    }

    this.investmentInCompaniesService.depositList(this.investment.uid)
      .then(list => {
        this.deposits = (Array.isArray(list) ? list : [])
          .slice()
          .sort((a, b) => new Date(b.depositDate).getTime() - new Date(a.depositDate).getTime());
        if (initial) this.resetForm();
        this.openRequestedEdit();
      })
      .catch(() => {
        if (initial) {
          this.loadError = true;
        } else {
          this.notify.error('No se pudieron recargar los pagos');
        }
      })
      .then(() => { this.ready = true; });
  }

  private openRequestedEdit(): void {
    if (!this.data.editUid || this.requestedEditDone) return;
    this.requestedEditDone = true;
    const deposit = this.deposits.find(item => item.uid === this.data.editUid);
    if (deposit) this.startEdit(deposit);
  }

  private lookupKind(typePay: string | null): InvestmentCompanyPayKind | null {
    switch (typePay) {
      case this.methods.transfer: return 'bankAccounts';
      case this.methods.card: return 'paymentCards';
      case this.methods.cash: return 'cashRegisters';
      case this.methods.partner: return 'partners';
      case this.methods.creditPolicy: return 'creditPolicies';
      default: return null;
    }
  }

  private ensureOptions(kind: InvestmentCompanyPayKind): void {
    if (this.optionRequests.has(kind)) return;
    this.loadingOptions[kind] = true;
    this.configs[kind] = { ...this.configs[kind], hint: 'Cargando…' };
    const request = this.lookups.payOptions(kind)
      .then(list => {
        this.options[kind] = list || [];
        this.loadedKinds.add(kind);
        this.configs[kind] = {
          ...this.configs[kind],
          options: this.options[kind],
          hint: this.options[kind].length ? undefined : this.emptyHints[kind],
        };
        this.applyPendingSelections();
      })
      .catch(() => {
        this.optionRequests.delete(kind);
        this.configs[kind] = { ...this.configs[kind], hint: undefined };
        this.notify.error('No se pudieron cargar las opciones del tipo de pago');
      })
      .then(() => { this.loadingOptions[kind] = false; });
    this.optionRequests.set(kind, request);
  }

  private rememberSelection(kind: InvestmentCompanyPayKind, id: any, label: string): void {
    if (id === null || id === undefined || id === '') return;
    this.pendingSelection.set(kind, { id, label: label || '' });
  }

  private applyPendingSelections(): void {
    this.pendingSelection.forEach((pending, kind) => {
      if (!this.loadedKinds.has(kind)) return;
      const option = this.findOption(this.options[kind], pending.id) || this.keepMissingOption(kind, pending);
      this.control(kind).setValue(option, { emitEvent: false });
      this.pendingSelection.delete(kind);
    });
  }

  private keepMissingOption(kind: InvestmentCompanyPayKind, pending: { id: any; label: string }): any {
    const text = String(pending.label || '').trim() || this.missingLabels[kind];
    const option = { id: pending.id, display: text + ' (no disponible)', disabled: true };
    this.options[kind] = [...this.options[kind], option];
    this.configs[kind] = { ...this.configs[kind], options: this.options[kind] };
    return option;
  }

  private findOption(options: { id: any }[], id: any): any {
    const key = String(id);
    return options.find(option => String(option.id) === key)
      || options.find(option => String(option.id).toLowerCase() === key.toLowerCase())
      || null;
  }

  private buildPayload(): InvestmentInCompaniesDepositsModel {
    const type = this.typePay;
    const payload = new InvestmentInCompaniesDepositsModel();
    payload.uid = this.editing ? this.editing.uid : null;
    payload.investmentsInCompaniesId = this.investment.uid;
    payload.typePay = type as string;
    payload.depositDate = formatDate(this.depositDateControl.value as Date, 'yyyy-MM-dd', 'es-ES') as any;
    payload.amount = roundMoney(Number(this.amountControl.value));
    payload.commissions = type === this.methods.transfer ? roundMoney(Number(this.commissionsControl.value) || 0) : 0;
    payload.bankAccountsUid = type === this.methods.transfer && this.selectedId('bankAccounts') ? this.selectedId('bankAccounts') : EMPTY_GUID;
    payload.paymentCardsUid = type === this.methods.card && this.selectedId('paymentCards') ? this.selectedId('paymentCards') : EMPTY_GUID;
    payload.efectivoId = type === this.methods.cash ? this.selectedId('cashRegisters') : null as any;
    payload.partnersId = type === this.methods.partner ? Number(this.selectedId('partners')) || 0 : 0;
    payload.polizaCreditoId = type === this.methods.creditPolicy ? this.selectedId('creditPolicies') : null as any;
    return payload;
  }

  private resetForm(): void {
    this.submitted = false;
    this.saveError = '';
    this.pendingSelection.clear();
    this.form.reset({
      typePay: null,
      depositDate: new Date(),
      amount: this.remaining > 0 ? this.remaining : null,
      commissions: null,
      bankAccount: null,
      paymentCard: null,
      cashRegister: null,
      partner: null,
      creditPolicy: null,
    }, { emitEvent: false });
  }

  private depositDetails(deposit: InvestmentInCompaniesDepositsModel): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [
      { label: 'Importe', value: this.formatMoney(deposit.amount) },
    ];
    if (deposit.depositDate) details.push({ label: 'Fecha', value: formatDate(deposit.depositDate, 'dd/MM/yyyy', 'es-ES') });
    if (deposit.typePay) details.push({ label: 'Forma de pago', value: deposit.typePay + (deposit.bankAccountInfo ? ' · ' + deposit.bankAccountInfo : '') });
    return details;
  }

  private guidOrNull(value: string): string | null {
    return value && value !== EMPTY_GUID ? value : null;
  }

  private hasValue(value: any): boolean {
    return value !== null && value !== undefined && value !== '';
  }
}
